/**
 * Global Coach Marks registry — wires the app-level Tour button (in
 * the Navbar) to whichever page is currently mounted.
 *
 * Pages that ship coach marks call `useRegisterTour(start)` on mount;
 * the hook stashes the `start` callback in the registry's state and
 * clears it on unmount. The Navbar reads `useTourStarter()` — when
 * null, the Tour button renders as disabled; when non-null, clicking
 * the button calls it.
 *
 * Why a registry instead of route-based config: the per-page coach-
 * marks state machine lives inside that page's component tree (so
 * the tour engine can read the page's view-mode / modal state). The
 * Navbar can't reach into a child's context, so we lift the `start`
 * callback up to a tiny shared registry instead. Net cost: one
 * `useEffect` per page that has a tour.
 *
 * Provider mounts inside `Layout.tsx` so both the Navbar and every
 * routed page sit under it.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * Public registry state. `start` is null when no page has registered
 * a tour for the current route. Mutation goes through `setStarter`
 * (used by `useRegisterTour` only — pages don't call it directly).
 */
interface CoachMarksRegistryValue {
  start: (() => void) | null;
  setStarter: (fn: (() => void) | null) => void;
}

const CoachMarksRegistryContext = createContext<CoachMarksRegistryValue | null>(
  null,
);

export const CoachMarksRegistryProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [start, setStart] = useState<(() => void) | null>(null);
  // Wrap the setter so callers can pass `(() => void) | null` directly
  // without React thinking it's a state-updater function (React treats
  // a function passed to `useState`'s setter as `prev => next`).
  const setStarter = useCallback((fn: (() => void) | null) => {
    setStart(() => fn);
  }, []);
  const value = useMemo<CoachMarksRegistryValue>(
    () => ({ start, setStarter }),
    [start, setStarter],
  );
  return (
    <CoachMarksRegistryContext.Provider value={value}>
      {children}
    </CoachMarksRegistryContext.Provider>
  );
};

/**
 * Page-side hook — registers the page's `start` callback on mount and
 * unregisters on unmount. Re-registers whenever the callback identity
 * changes so the latest closure is what runs when the user clicks the
 * Tour button.
 *
 * Defensive no-op when the provider isn't mounted (e.g. unit tests
 * that render a page bare) — caller still works, the button just
 * never lights up.
 */
export const useRegisterTour = (start: () => void): void => {
  const ctx = useContext(CoachMarksRegistryContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setStarter(start);
    return () => ctx.setStarter(null);
  }, [ctx, start]);
};

/**
 * Navbar-side hook — returns the current registered starter or `null`.
 * The Tour button enables/disables based on this.
 */
export const useTourStarter = (): (() => void) | null => {
  const ctx = useContext(CoachMarksRegistryContext);
  return ctx?.start ?? null;
};
